import { DIVIDER_CDT_LEVELS, type DividerCdtLevel } from "@CommonTypes/backend/PspRules";
import { dividerAttributeLabel } from "@CommonUtils/pog/dividerPolicy";
import {
  tellOGroupValue,
  tellOLevelLabel,
  type TellOLevelNames,
  type TellOProduct,
} from "./products";
import { TELL_O_MAX_CANDIDATES, type TellOGroup } from "./proposal";

/** Attributes with more values than this are names or ids, not groups anyone asks for. */
const MAX_ATTRIBUTE_VALUES = 40;
const MIN_NAME_WORD_LENGTH = 3;

/** Words that say what to do, not which products; never a product reference. */
const NON_PRODUCT_WORDS = new Set(
  (
    "a an the and or but of to for on in at by with without from into onto off " +
    "no not none nor never dont don't do does only just except also too all every everything each " +
    "any some this that these those them their theirs they it its me my i we our you please " +
    "want like need needs make put get gets give take away remove removing add adding set use " +
    "keep keeps stay stays follow follows should would could can will be is are was were " +
    "divider dividers spring springs loader loaders pusher pushers product products item items " +
    "shelf shelves store stores bay bays fixture fixtures hardware rule rules wide thick deep " +
    "width depth inch inches cm mm instead same other others rest"
  ).split(" "),
);

/** Lower case, words only, each made singular: "Roll-Ons" and "roll on" read alike. */
export const tellOWords = (text: string): string[] =>
  text
    .toLowerCase()
    .replace(/[’`]/g, "'")
    .replace(/'s\b/g, "")
    .split(/[^a-z0-9']+/)
    .map((word) => word.replace(/'/g, ""))
    .filter(Boolean)
    .map(singular);

const singular = (word: string) => {
  if (word.length <= 3 || /\d/.test(word)) return word;
  if (word.endsWith("ies")) return `${word.slice(0, -3)}y`;
  if (/(ss|sh|ch|x|z)es$/.test(word)) return word.slice(0, -2);
  if (word.endsWith("s") && !word.endsWith("ss")) return word.slice(0, -1);
  return word;
};

export const tellOGroupLabel = (group: TellOGroup): string => {
  switch (group.kind) {
    case "cdt":
    case "attribute":
      return group.value;
    case "name":
      return group.word;
    case "product":
      return group.name || group.productCode;
    case "position":
      return group.position === "top"
        ? "the base planogram's top shelves"
        : group.position === "bottom"
          ? "the base planogram's bottom shelves"
          : `the base planogram's shelf ${group.position}`;
  }
};

export interface TellODictionary {
  products: readonly TellOProduct[];
  /** Every CDT value of every level, attribute value, shared name word and product. */
  groups: TellOGroup[];
}

const addTo = (map: Map<string, { value: string; codes: string[] }>, value: string, code: string) => {
  const key = tellOWords(value).join(" ");
  if (!key) return;
  const entry = map.get(key) ?? { value, codes: [] };
  entry.codes.push(code);
  map.set(key, entry);
};

/** B.2: what a product reference may resolve to, all from the window's own data. */
export const buildTellODictionary = ({
  products,
  levelNames = {},
}: {
  products: readonly TellOProduct[];
  levelNames?: TellOLevelNames;
}): TellODictionary => {
  const groups: TellOGroup[] = [];

  for (const level of DIVIDER_CDT_LEVELS) {
    const values = new Map<string, { value: string; codes: string[] }>();
    for (const product of products) {
      const value = tellOGroupValue(product, level);
      if (value) addTo(values, value, product.productCode);
    }
    for (const { value, codes } of values.values()) {
      groups.push({
        kind: "cdt",
        level,
        levelLabel: tellOLevelLabel(level, levelNames),
        value,
        productCodes: codes,
      });
    }
  }

  const attributes = new Set<string>();
  for (const product of products) {
    for (const key of Object.keys(product.attributes ?? {})) attributes.add(key);
  }
  for (const attribute of [...attributes].sort()) {
    const values = new Map<string, { value: string; codes: string[] }>();
    for (const product of products) {
      const raw = product.attributes?.[attribute];
      if (raw === null || raw === undefined || typeof raw === "boolean") continue;
      const value = String(raw).trim();
      if (value) addTo(values, value, product.productCode);
    }
    if (values.size < 2 || values.size > MAX_ATTRIBUTE_VALUES) continue;
    for (const { value, codes } of values.values()) {
      groups.push({
        kind: "attribute",
        attribute,
        attributeLabel: dividerAttributeLabel(attribute),
        value,
        productCodes: codes,
      });
    }
  }

  const nameWords = new Map<string, { word: string; codes: Set<string> }>();
  for (const product of products) {
    for (const raw of (product.name ?? "").split(/[^A-Za-z0-9'’]+/)) {
      const [word] = tellOWords(raw);
      if (!word || word.length < MIN_NAME_WORD_LENGTH || /^\d+$/.test(word)) continue;
      if (NON_PRODUCT_WORDS.has(word)) continue;
      const entry = nameWords.get(word) ?? { word: raw.replace(/[’']s$/, ""), codes: new Set() };
      entry.codes.add(product.productCode);
      nameWords.set(word, entry);
    }
  }
  // A name word that picks exactly a CDT or attribute value's products adds nothing.
  const groupSets = new Set(groups.map((group) => [...group.productCodes].sort().join("|")));
  for (const { word, codes } of nameWords.values()) {
    if (codes.size < 2 || codes.size >= products.length) continue;
    const productCodes = inProductOrder(products, codes);
    if (groupSets.has([...productCodes].sort().join("|"))) continue;
    groups.push({ kind: "name", word, productCodes });
  }

  for (const product of products) {
    groups.push({
      kind: "product",
      productCode: product.productCode,
      name: product.name ?? null,
      productCodes: [product.productCode],
    });
  }
  return { products, groups };
};

const inProductOrder = (products: readonly TellOProduct[], codes: ReadonlySet<string>) =>
  products.map((product) => product.productCode).filter((code) => codes.has(code));

export interface TellOCandidate {
  /** The option key the model chooses; never shown. */
  id: string;
  group: TellOGroup;
  /** Words of the manager's text the candidate was found by, or the text's product words. */
  phrase: string;
  /** 1 = the exact words; 0 = offered for meaning only (no words in common). */
  match: number;
}

/** The text's words that may name products: what is left after the words of the ask. */
export const tellOProductPhrase = (text: string): string =>
  text
    .split(/\s+/)
    .map((word) => word.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, ""))
    .filter((word) => word && !NON_PRODUCT_WORDS.has(tellOWords(word)[0] ?? word.toLowerCase()))
    .join(" ");

const editDistanceAtMostOne = (a: string, b: string) => {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i += 1;
      j += 1;
      continue;
    }
    edits += 1;
    if (edits > 1) return false;
    if (a.length > b.length) i += 1;
    else if (b.length > a.length) j += 1;
    else {
      i += 1;
      j += 1;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
};

const groupWords = (group: TellOGroup): string[] =>
  group.kind === "product" ? [] : tellOWords(tellOGroupLabel(group));

/** The best match of a group's words inside the text's words, with the matched original words. */
const matchGroup = (
  group: TellOGroup,
  textWords: readonly string[],
  originals: readonly string[],
): { match: number; phrase: string } | null => {
  if (group.kind === "product") {
    const code = group.productCode.toLowerCase();
    const at = originals.findIndex((word) => word.toLowerCase() === code);
    return at >= 0 ? { match: 1, phrase: originals[at] } : null;
  }
  const words = groupWords(group);
  if (words.length === 0) return null;
  const joined = singular(words.join(""));
  let best: { match: number; phrase: string } | null = null;
  for (let start = 0; start < textWords.length; start += 1) {
    for (let length = 1; length <= Math.min(3, textWords.length - start); length += 1) {
      const slice = textWords.slice(start, start + length);
      if (slice.some((word) => NON_PRODUCT_WORDS.has(word))) continue;
      const phrase = originals.slice(start, start + length).join(" ");
      let match = 0;
      if (slice.join(" ") === words.join(" ")) match = 1;
      else if (singular(slice.join("")) === joined) match = 0.95;
      else if (joined.length >= 5 && editDistanceAtMostOne(singular(slice.join("")), joined)) {
        match = 0.8;
      }
      else if (
        length === 1 &&
        words.length > 1 &&
        slice[0].length >= 4 &&
        words.includes(slice[0])
      ) {
        match = 0.6;
      }
      if (match > 0 && (!best || match > best.match)) best = { match, phrase };
    }
  }
  return best;
};

/**
 * Groups whose words appear in the text, then CDT groups by size so a reference by meaning
 * ("sprays" for Aerosols) is in the list: the model only chooses among these.
 */
export const findTellOCandidates = (
  dictionary: TellODictionary,
  text: string,
  limit = TELL_O_MAX_CANDIDATES,
): TellOCandidate[] => {
  const originals = text
    .split(/[^A-Za-z0-9'’-]+/)
    .map((word) => word.replace(/^[-']+|[-']+$/g, ""))
    .filter(Boolean);
  const textWords = originals.map((word) => tellOWords(word).join("") || word.toLowerCase());
  const matched: { group: TellOGroup; phrase: string; match: number }[] = [];
  for (const group of dictionary.groups) {
    const found = matchGroup(group, textWords, originals);
    if (found) matched.push({ group, ...found });
  }
  matched.sort(
    (a, b) => b.match - a.match || b.group.productCodes.length - a.group.productCodes.length,
  );

  const all = dictionary.products.length;
  const seen = new Set(matched.map(({ group }) => groupKey(group)));
  const productPhrase = tellOProductPhrase(text);
  const byMeaning = dictionary.groups
    .filter(
      (group): group is Extract<TellOGroup, { kind: "cdt" }> =>
        group.kind === "cdt" &&
        group.productCodes.length < all &&
        !seen.has(groupKey(group)),
    )
    .sort(
      (a, b) =>
        cdtLevelRank(a.level) - cdtLevelRank(b.level) ||
        b.productCodes.length - a.productCodes.length,
    )
    .map((group) => ({ group, phrase: productPhrase, match: 0 }));

  return [...matched, ...byMeaning].slice(0, limit).map((candidate, index) => ({
    id: `c${index + 1}`,
    ...candidate,
  }));
};

/** Level 2 first: Level 1 is usually the whole category. */
const cdtLevelRank = (level: DividerCdtLevel) =>
  ({ cdt1: 0, cdt2: 1, cdt0: 2, cdt3: 3 })[level];

const groupKey = (group: TellOGroup) =>
  group.kind === "cdt"
    ? `cdt:${group.level}:${group.value}`
    : group.kind === "attribute"
      ? `attribute:${group.attribute}:${group.value}`
      : group.kind === "name"
        ? `name:${group.word}`
        : group.kind === "product"
          ? `product:${group.productCode}`
          : `position:${group.position}`;

/**
 * A.1's {G}: the largest Level 1 group that is not the whole category, else
 * the largest Level 2 one; null when neither level splits the products.
 */
export const largestTellOGroup = (
  dictionary: TellODictionary,
): Extract<TellOGroup, { kind: "cdt" }> | null => {
  const all = dictionary.products.length;
  for (const level of ["cdt0", "cdt1"] as const) {
    const groups = dictionary.groups.filter(
      (group): group is Extract<TellOGroup, { kind: "cdt" }> =>
        group.kind === "cdt" && group.level === level && group.productCodes.length < all,
    );
    if (groups.length === 0) continue;
    return groups.reduce((best, group) =>
      group.productCodes.length > best.productCodes.length ? group : best,
    );
  }
  return null;
};
